'use client';

import { useMemo } from 'react';
import { Box, Container, Grid, Group, Paper, SimpleGrid, Stack, Text, TypographyStylesProvider } from '@mantine/core';
import type { Locale } from '../../i18n/locales';
import type { ContentPageData, SiteSettingsData } from '../../types/content';
import { Header } from '../layout/Header';
import { Footer } from '../layout/Footer';
import { PublicPageShell } from '../layout/PublicPageShell';
import { HeroBlock } from './blocks/HeroBlock';
import { JsonLd } from '../JsonLd';
import { resolvePublicHref } from '../../lib/content/comparisonLinks';
import { siteUrl } from '../../lib/site';
import { AutovakuutusSidebar } from './AutovakuutusSidebar';
import type { AutovakuutusSubpageLink } from '../../lib/content/autovakuutus';
import { PageWidgetsRenderer } from './widgets/PageWidgetsRenderer';
import type { PageWidget, PageWidgetKey } from '../../types/content';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import { MantineNextImage } from '../ui/MantineNextImage';
import {
  resolveContentPageHeroSubtitle,
  shouldRenderStandaloneContentPageBody,
  stripHtmlToPlainText,
} from '../../lib/content/contentPagePresentation';

const normalizeInternalPath = (value?: string) => {
  if (!value) return '';
  const noQuery = value.split('?')[0]?.split('#')[0] || '';
  let normalized = noQuery.trim();
  if (!normalized.startsWith('/')) normalized = `/${normalized}`;
  normalized = normalized.replace(/^\/(fi|sv|en)(?=\/|$)/, '');
  normalized = normalized.replace(/\/+$/, '');
  return normalized || '/';
};

const normalizeImageUrl = (value?: string): string | undefined => {
  if (!value) return undefined;
  let normalized = value.trim();
  if (!normalized) return undefined;

  if (
    (normalized.startsWith('"') && normalized.endsWith('"')) ||
    (normalized.startsWith('\'') && normalized.endsWith('\''))
  ) {
    normalized = normalized.slice(1, -1).trim();
  }

  normalized = normalized.replace(/\\\//g, '/');
  if (!normalized || /^data:image\//i.test(normalized)) return undefined;
  if (normalized.startsWith('//')) return `https:${normalized}`;
  if (/^https?:\/\//i.test(normalized) || normalized.startsWith('/')) return normalized;
  return undefined;
};

const firstImageFromHtml = (html?: string): string | undefined => {
  if (!html) return undefined;
  const matches = html.match(/<img[^>]*>/gi);
  if (!matches?.length) return undefined;

  for (const imageTag of matches) {
    const srcMatch = imageTag.match(/src=["']([^"']+)["']/i);
    const dataSrcMatch =
      imageTag.match(/data-src=["']([^"']+)["']/i) ||
      imageTag.match(/data-lazy-src=["']([^"']+)["']/i) ||
      imageTag.match(/data-original=["']([^"']+)["']/i);
    const src = normalizeImageUrl(srcMatch?.[1]) || normalizeImageUrl(dataSrcMatch?.[1]);
    if (src) return src;
  }

  return undefined;
};

const isImageOnlyPlaceholderTitle = (value?: string) => /^image\s+\d+$/i.test((value || '').trim());

export function ContentPageView({
  data,
  locale,
  siteSettings,
  autovakuutusSubpages,
  activeAutovakuutusSlug,
  sidebarRootHref,
  sidebarRootLabel,
  sidebarTitle,
  activeSidebarHref,
  variant = 'default',
  widgets,
  pageWidgetKey,
}: {
  data: ContentPageData;
  locale: Locale;
  siteSettings: SiteSettingsData;
  autovakuutusSubpages?: AutovakuutusSubpageLink[];
  activeAutovakuutusSlug?: string;
  sidebarRootHref?: string;
  sidebarRootLabel?: string;
  sidebarTitle?: string;
  activeSidebarHref?: string;
  variant?: 'default' | 'product';
  widgets?: PageWidget[];
  pageWidgetKey?: PageWidgetKey;
}) {
  const primaryCtaLabel = data.primaryCtaLabel;
  const primaryCtaHref = data.primaryCtaHref;
  const plainBody = stripHtmlToPlainText(data.body);

  const hasSidebarNavigation = Boolean(autovakuutusSubpages?.length);
  const sidebarParentHref = sidebarRootHref || '/autovakuutus';
  const defaultSidebarRootLabel =
    locale === 'fi' ? 'Autovakuutus' : locale === 'sv' ? 'Bilförsäkring' : 'Car insurance';
  const sidebarRoot = {
    slug: sidebarParentHref.replace(/^\/+/, '') || 'autovakuutus',
    href: sidebarParentHref,
    label: sidebarRootLabel || defaultSidebarRootLabel,
  };

  const derivedActiveHref = activeAutovakuutusSlug
    ? autovakuutusSubpages?.find((item) => item.slug === activeAutovakuutusSlug)?.href
    : undefined;
  const resolvedActiveSidebarHref = activeSidebarHref || derivedActiveHref;

  const sections = (data.sections || []).filter((section) => section.title || section.body || section.imageUrl);
  const sectionsWithText = sections.filter((section) => {
    const title = (section.title || '').trim();
    const bodyText = stripHtmlToPlainText(section.body);
    const hasMeaningfulTitle = Boolean(title) && !isImageOnlyPlaceholderTitle(title);
    return hasMeaningfulTitle || Boolean(bodyText);
  });
  const renderableSections = sectionsWithText;
  const hasStructuredSections = renderableSections.length > 0;
  const heroSubtitle = resolveContentPageHeroSubtitle({
    heroSubtitle: data.heroSubtitle,
    body: data.body,
    hasStructuredSections,
  });
  const normalizedSidebarRoot = normalizeInternalPath(sidebarParentHref);
  const heroBackgroundImage =
    normalizeImageUrl(data.heroImageUrl) ||
    firstImageFromHtml(data.body) ||
    normalizeImageUrl((data.sections || []).find((section) => normalizeImageUrl(section.imageUrl))?.imageUrl) ||
    normalizeImageUrl(data.ogImageUrl);

  const shouldRenderBody = shouldRenderStandaloneContentPageBody({
    body: data.body,
    hasStructuredSections,
  });
  const isSidebarRoot =
    hasSidebarNavigation &&
    (resolvedActiveSidebarHref
      ? normalizeInternalPath(resolvedActiveSidebarHref) === normalizeInternalPath(sidebarParentHref)
      : true);

  const pagePath = hasSidebarNavigation ? resolvedActiveSidebarHref || sidebarParentHref : `/${data.slug}`;
  const showSidebarHint =
    isSidebarRoot &&
    normalizedSidebarRoot !== '/autovakuutus' &&
    normalizedSidebarRoot !== '/kotivakuutus' &&
    normalizedSidebarRoot !== '/lemmikkielainvakuutus';
  const breadcrumbItems = [
    { name: 'Etusivu', href: `${siteUrl}/` },
    { name: data.title, href: `${siteUrl}${pagePath}` },
  ];

  const primarySection = renderableSections[0];
  const remainingSections = renderableSections.slice(1);
  const resolvedWidgets = widgets || [];
  const refreshSubscriptions = useMemo(() => {
    const subscriptions = [
      { table: 'content_pages', filter: `slug=eq.${data.slug}` },
      { table: 'content_page_sections', filter: `page_slug=eq.${data.slug}` },
    ];

    if (pageWidgetKey) {
      subscriptions.push({ table: 'page_widgets', filter: `page_key=eq.${pageWidgetKey}` });
    }

    return subscriptions;
  }, [data.slug, pageWidgetKey]);
  useRealtimeRefresh(refreshSubscriptions);

  const productContent = (
    <Container size="xl" py={56}>
      <Stack gap={20}>
        {primarySection ? (
          <Paper p={{ base: 24, md: 40 }} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-base)' }}>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={32}>
              <Stack gap={12}>
                <Box
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 999,
                    background: 'var(--brand-primary)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                  }}
                >
                  −
                </Box>
                <Text style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.9rem, 3.8vw, 2.5rem)', lineHeight: 1.3, fontWeight: 500 }}>
                  {primarySection.title || data.title}
                </Text>
              </Stack>

              <Stack gap={14}>
                <Text style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }}>
                  {primarySection.body || plainBody || heroSubtitle}
                </Text>

                {remainingSections.length ? (
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={8}>
                    {remainingSections.slice(0, 6).map((item, idx) => (
                      <Text key={`${item.title}-${idx}`} size="sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                        • {item.title || stripHtmlToPlainText(item.body).substring(0, 70)}
                      </Text>
                    ))}
                  </SimpleGrid>
                ) : null}
              </Stack>
            </SimpleGrid>
          </Paper>
        ) : null}

        {remainingSections.map((section, idx) => (
          <Paper
            key={`${section.title}-${idx}`}
            p={{ base: 20, md: 30 }}
            radius={999}
            withBorder
            style={{
              borderColor: 'var(--surface-border)',
              background:
                idx % 3 === 0 ? 'var(--surface-muted)' : idx % 3 === 1 ? 'var(--surface-alt)' : 'var(--surface-soft)',
            }}
          >
            <Group gap={14}>
              <Box
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 999,
                  background: 'var(--brand-primary)',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                }}
              >
                +
              </Box>
              <Text style={{ color: 'var(--text-primary)', fontSize: 'clamp(1.45rem, 3vw, 2.25rem)', lineHeight: 1.3, fontWeight: 500 }}>
                  {section.title}
              </Text>
            </Group>
          </Paper>
        ))}

        {shouldRenderBody ? (
          <Paper p={{ base: 22, md: 30 }} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: '#fff' }}>
            <TypographyStylesProvider>
              <div className="content-rich-text" dangerouslySetInnerHTML={{ __html: data.body }} />
            </TypographyStylesProvider>
          </Paper>
        ) : null}

        {hasSidebarNavigation ? (
          <Paper p={24} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-base)' }}>
            <Stack gap={10}>
              <Text fw={600} style={{ color: 'var(--text-primary)' }}>
                {sidebarTitle || (locale === 'fi' ? 'Lisäoppaat' : locale === 'sv' ? 'Extra guider' : 'Additional guides')}
              </Text>
              <Group gap={10} wrap="wrap">
                {(autovakuutusSubpages || []).map((item) => (
                  <Text
                    key={item.slug}
                    style={{
                      border: '1px solid var(--surface-border)',
                      borderRadius: 999,
                      padding: '8px 14px',
                      background: 'var(--surface-soft)',
                    }}
                  >
                    {item.label}
                  </Text>
                ))}
              </Group>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Container>
  );

  const defaultContent = hasSidebarNavigation ? (
    <Container size="xl" py={56}>
      <Grid gutter={24} align="start">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap={20}>
            {showSidebarHint ? (
              <Paper p={24} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: 'var(--surface-soft)' }}>
                <Text size="sm" style={{ color: 'var(--text-secondary)' }}>
                  {locale === 'fi'
                    ? 'Sivupalkista löydät pääsivun sekä tärkeimmät alasivut.'
                    : locale === 'sv'
                      ? 'Sidopanelen innehåller huvudsidan och de viktigaste undersidorna.'
                      : 'The sidebar includes the main page and key subpages.'}
                </Text>
              </Paper>
            ) : null}

            {shouldRenderBody ? (
              <Paper p={{ base: 22, md: 30 }} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: '#fff' }}>
                <TypographyStylesProvider>
                  <div className="content-rich-text" dangerouslySetInnerHTML={{ __html: data.body }} />
                </TypographyStylesProvider>
              </Paper>
            ) : null}

            {renderableSections.length ? (
              <Stack gap={20}>
                {renderableSections.map((section, idx) => (
                  <Paper
                    key={`${section.title}-${idx}`}
                    p={{ base: 20, md: 30 }}
                    radius={40}
                    withBorder
                    style={{
                      borderColor: 'var(--surface-border)',
                      background: idx % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-muted)',
                    }}
                  >
                    <SimpleGrid cols={{ base: 1, md: section.imageUrl ? 2 : 1 }} spacing={24}>
                      <Stack gap="sm" style={{ order: idx % 2 !== 0 ? 2 : 1 }}>
                        <Text fw={600} size="xl" style={{ color: 'var(--text-primary)' }}>
                          {section.title}
                        </Text>
                        <TypographyStylesProvider>
                          <Text size="md" style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }} component="div">
                            <div dangerouslySetInnerHTML={{ __html: section.body || '' }} />
                          </Text>
                        </TypographyStylesProvider>
                      </Stack>
                      {section.imageUrl ? (
                        <Box style={{ order: idx % 2 !== 0 ? 1 : 2 }}>
                          <MantineNextImage src={section.imageUrl} alt={section.imageAlt || section.title} radius={30} />
                        </Box>
                      ) : null}
                    </SimpleGrid>
                  </Paper>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box style={{ position: 'sticky', top: 100 }}>
            <AutovakuutusSidebar
              locale={locale}
              items={autovakuutusSubpages || []}
              activeHref={resolvedActiveSidebarHref}
              title={sidebarTitle}
              rootEntry={sidebarRoot}
            />
          </Box>
        </Grid.Col>
      </Grid>
    </Container>
  ) : (
    <Container size="xl" py={56}>
      <Stack gap={20}>
        {shouldRenderBody ? (
          <Paper p={{ base: 22, md: 30 }} radius={40} withBorder style={{ borderColor: 'var(--surface-border)', background: '#fff' }}>
            <TypographyStylesProvider>
              <div className="content-rich-text" dangerouslySetInnerHTML={{ __html: data.body }} />
            </TypographyStylesProvider>
          </Paper>
        ) : null}

        {renderableSections.length
          ? renderableSections.map((section, idx) => (
            <Paper
              key={`${section.title}-${idx}`}
              p={{ base: 20, md: 30 }}
              radius={40}
              withBorder
              style={{
                borderColor: 'var(--surface-border)',
                background: idx % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-muted)',
              }}
            >
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing={24}>
                <Stack gap="sm" style={{ order: idx % 2 !== 0 ? 2 : 1 }}>
                  <Text fw={600} size="xl" style={{ color: 'var(--text-primary)' }}>
                    {section.title}
                  </Text>
                  <TypographyStylesProvider>
                    <Text size="md" style={{ color: 'var(--text-secondary)', lineHeight: 1.75 }} component="div">
                      <div dangerouslySetInnerHTML={{ __html: section.body || '' }} />
                    </Text>
                  </TypographyStylesProvider>
                </Stack>
                <Box style={{ order: idx % 2 !== 0 ? 1 : 2 }}>
                  {section.imageUrl ? (
                    <MantineNextImage src={section.imageUrl} alt={section.imageAlt || section.title} radius={30} />
                  ) : (
                    <Paper radius={30} h={220} style={{ background: 'var(--surface-soft)', border: '1px solid var(--surface-border)' }} />
                  )}
                </Box>
              </SimpleGrid>
            </Paper>
          ))
          : null}
      </Stack>
    </Container>
  );

  return (
    <PublicPageShell>
      <Header locale={locale} settings={siteSettings} />

      <HeroBlock
        title={data.title}
        subtitle={heroSubtitle}
        eyebrow={data.eyebrow}
        primaryCtaLabel={primaryCtaLabel}
        primaryCtaHref={primaryCtaHref ? resolvePublicHref(locale, primaryCtaHref) : undefined}
        secondaryCtaLabel={data.secondaryCtaLabel}
        secondaryCtaHref={data.secondaryCtaHref ? resolvePublicHref(locale, data.secondaryCtaHref) : undefined}
        trustLabel={!data.secondaryCtaHref ? data.secondaryCtaLabel : undefined}
        backgroundImage={variant === 'product' ? undefined : heroBackgroundImage}
        locale={locale}
      />

      {variant === 'product' ? productContent : defaultContent}
      {resolvedWidgets.length ? <PageWidgetsRenderer locale={locale} widgets={resolvedWidgets} /> : null}

      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.href,
          })),
        }}
      />

      <Footer locale={locale} settings={siteSettings} />
    </PublicPageShell>
  );
}
